import { createSignal } from 'solid-js';
import { uploadFiles } from '../state/actions';
import { pushToast } from './uiState';

const REJECT_TEXT: Record<'unsupported-type' | 'load-failed', string> = {
  'unsupported-type': 'unsupported format',
  'load-failed': 'failed to read',
};

async function handleFiles(files: File[]) {
  if (files.length === 0) return;
  const outcome = await uploadFiles(files);
  for (const name of outcome.rejectedFilenames) {
    const reason = outcome.rejectedReasons[name] ?? 'load-failed';
    pushToast({ kind: 'error', text: `${name}: ${REJECT_TEXT[reason]}` });
  }
  if (outcome.skippedFilenames.length > 0) {
    pushToast({
      kind: 'info',
      text: `Sheet is full, did not fit: ${outcome.skippedFilenames.join(', ')}`,
    });
  }
}

export function DropZone() {
  let inputRef!: HTMLInputElement;
  const [isDragging, setIsDragging] = createSignal(false);

  function openPicker() {
    inputRef.click();
  }

  function onDragOver(e: DragEvent) {
    e.preventDefault();
    setIsDragging(true);
  }

  function onDragEnter(e: DragEvent) {
    e.preventDefault();
    setIsDragging(true);
  }

  function onDragLeave() {
    setIsDragging(false);
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      void handleFiles(Array.from(files));
    }
  }

  function onClick(e: MouseEvent) {
    if (e.target === inputRef) return;
    openPicker();
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  }

  function onInputChange() {
    const files = inputRef.files;
    if (files && files.length > 0) {
      void handleFiles(Array.from(files));
    }
    inputRef.value = '';
  }

  return (
    <div
      class={`drop-zone${isDragging() ? ' drop-zone--dragging' : ''}`}
      role="button"
      aria-label="Add photo"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/svg+xml"
        style={{ display: 'none' }}
        onChange={onInputChange}
      />
      <span class="drop-zone__icon">+</span>
      <span class="drop-zone__label">Add photo</span>
      <span class="drop-zone__hint">jpeg · png · svg</span>
    </div>
  );
}
